import { TextField, Flex, Text, Card } from "@radix-ui/themes";
import { ChevronDownIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useState, useCallback } from "react";
import { ValidationEditorProps, PropertyValidation } from "./types";
import Select from "@/components/shared/select/Select";
import Switch from "@/components/shared/inputs/switch";

const FORMAT_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "date-time", label: "Date-Time" },
  { value: "email", label: "Email" },
  { value: "hostname", label: "Hostname" },
  { value: "ipv4", label: "IPv4" },
  { value: "ipv6", label: "IPv6" },
  { value: "uri", label: "URI" },
  { value: "uuid", label: "UUID" },
];

export function ValidationEditor({
  validation,
  type,
  onChange,
}: ValidationEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const updateValidation = useCallback(
    (updates: Partial<PropertyValidation>) => {
      onChange({ ...validation, ...updates });
    },
    [validation, onChange]
  );

  const handleStringValidation = useCallback(
    (field: keyof PropertyValidation, value: string) => {
      const numValue = value === "" ? undefined : Number(value);
      updateValidation({ [field]: numValue });
    },
    [updateValidation]
  );

  const handleNumberValidation = useCallback(
    (field: keyof PropertyValidation, value: string) => {
      const numValue = value === "" ? undefined : Number(value);
      updateValidation({ [field]: numValue });
    },
    [updateValidation]
  );

  const handleBooleanValidation = useCallback(
    (field: keyof PropertyValidation, checked: boolean) => {
      updateValidation({ [field]: checked });
    },
    [updateValidation]
  );

  const handlePatternChange = useCallback(
    (pattern: string) => {
      updateValidation({ pattern: pattern || undefined });
    },
    [updateValidation]
  );

  const handleFormatChange = useCallback(
    (format: string) => {
      updateValidation({ format: format || undefined });
    },
    [updateValidation]
  );

  const isStringType = type === "string";
  const isNumberType = type === "number" || type === "integer";
  const isArrayType = type === "array";
  const isObjectType = type === "object";

  return (
    <Card className="validation-editor">
      <Collapsible.Root open={isExpanded}>
        <Collapsible.Trigger asChild>
          <Flex
            align="center"
            gap="2"
            className="cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            <Text size="2" weight="medium">
              Validation Rules
            </Text>
          </Flex>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Flex direction="column" gap="3" className="mt-3">
            {/* String Validation */}
            {isStringType && (
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">
                  String Validation
                </Text>
                <Flex gap="2">
                  <TextField.Root
                    placeholder="Min Length"
                    type="number"
                    value={validation.minLength?.toString() || ""}
                    onChange={(e) =>
                      handleStringValidation("minLength", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <TextField.Root
                    placeholder="Max Length"
                    type="number"
                    value={validation.maxLength?.toString() || ""}
                    onChange={(e) =>
                      handleStringValidation("maxLength", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                </Flex>
                <TextField.Root
                  placeholder="Pattern (regex)"
                  value={validation.pattern || ""}
                  onChange={(e) => handlePatternChange(e.target.value)}
                />
                <Flex gap="2" align="center">
                  <Text size="1">Format:</Text>
                  <div style={{ flex: 1 }}>
                    <Select
                      options={[
                        { value: "", label: "None" },
                        ...FORMAT_OPTIONS,
                      ]}
                      value={validation.format || ""}
                      onChange={handleFormatChange}
                      size="medium"
                    />
                  </div>
                </Flex>
              </Flex>
            )}

            {/* Number Validation */}
            {isNumberType && (
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">
                  Number Validation
                </Text>
                <Flex gap="2">
                  <TextField.Root
                    placeholder="Minimum"
                    type="number"
                    value={validation.minimum?.toString() || ""}
                    onChange={(e) =>
                      handleNumberValidation("minimum", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <TextField.Root
                    placeholder="Maximum"
                    type="number"
                    value={validation.maximum?.toString() || ""}
                    onChange={(e) =>
                      handleNumberValidation("maximum", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                </Flex>
                <Flex gap="2">
                  <TextField.Root
                    placeholder="Exclusive Minimum"
                    type="number"
                    value={validation.exclusiveMinimum?.toString() || ""}
                    onChange={(e) =>
                      handleNumberValidation("exclusiveMinimum", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <TextField.Root
                    placeholder="Exclusive Maximum"
                    type="number"
                    value={validation.exclusiveMaximum?.toString() || ""}
                    onChange={(e) =>
                      handleNumberValidation("exclusiveMaximum", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                </Flex>
                <TextField.Root
                  placeholder="Multiple Of"
                  type="number"
                  value={validation.multipleOf?.toString() || ""}
                  onChange={(e) =>
                    handleNumberValidation("multipleOf", e.target.value)
                  }
                />
              </Flex>
            )}

            {/* Array Validation */}
            {isArrayType && (
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">
                  Array Validation
                </Text>
                <Flex gap="2">
                  <TextField.Root
                    placeholder="Min Items"
                    type="number"
                    value={validation.minItems?.toString() || ""}
                    onChange={(e) =>
                      handleStringValidation("minItems", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <TextField.Root
                    placeholder="Max Items"
                    type="number"
                    value={validation.maxItems?.toString() || ""}
                    onChange={(e) =>
                      handleStringValidation("maxItems", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                </Flex>
                <Flex align="center" gap="2">
                  <Switch
                    value={validation.uniqueItems || false}
                    onChange={(checked) =>
                      handleBooleanValidation("uniqueItems", checked)
                    }
                  />
                  <Text size="1">Unique Items</Text>
                </Flex>
              </Flex>
            )}

            {/* Object Validation */}
            {isObjectType && (
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">
                  Object Validation
                </Text>
                <Flex gap="2">
                  <TextField.Root
                    placeholder="Min Properties"
                    type="number"
                    value={validation.minProperties?.toString() || ""}
                    onChange={(e) =>
                      handleStringValidation("minProperties", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <TextField.Root
                    placeholder="Max Properties"
                    type="number"
                    value={validation.maxProperties?.toString() || ""}
                    onChange={(e) =>
                      handleStringValidation("maxProperties", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
        </Collapsible.Content>
      </Collapsible.Root>
    </Card>
  );
}
